/* ── Analytics: publish to Shopify Customer Events (custom pixels) + GTM dataLayer if present ── */
function trackShopTheLook(eventName, data) {
  try {
    window.Shopify?.analytics?.publish?.(eventName, data);
  } catch (e) { /* analytics unavailable */ }
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...data });
  }
}

if (!customElements.get('pdp-shop-the-look-modal')) {
  class PdpShopTheLookModal extends HTMLElement {
    constructor() {
      super();
      this._opener = null;
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.querySelector('.pdp-stl-modal__close')
        .addEventListener('click', () => this.close());

      /* Close on backdrop click */
      this.addEventListener('click', e => {
        if (!e.target.closest('.pdp-stl-modal__dialog')) {
          this.close();
          return;
        }
        /* ATC — skip if button is in BIS mode (Klaviyo handles that click) */
        const atcBtn = e.target.closest('[data-stl-atc]:not([disabled]):not(.klaviyo-bis-trigger)');
        if (atcBtn) this._addToCart(atcBtn);
      });

      /* Variant resolution via the same radio inputs as PDP */
      this.addEventListener('change', e => {
        const input = e.target.closest('[data-stl-item] .product-option__input');
        if (!input) return;
        const item = input.closest('[data-stl-item]');
        this._updateAtcBtn(item, this._resolveVariant(item));
      });

      /* Initialise ATC button state for items that are pre-selected in Liquid */
      this.querySelectorAll('[data-stl-item]').forEach(item => {
        const atcBtn = item.querySelector('[data-stl-atc]');
        if (!atcBtn?.dataset.variantId) return;
        const variants = this._getVariants(item);
        const variant  = variants.find(v => v.id === parseInt(atcBtn.dataset.variantId));
        if (variant) this._updateAtcBtn(item, variant);
      });
    }

    open(openerEl, galleryImgHTML) {
      this._opener = openerEl || null;

      if (galleryImgHTML) {
        const container = this.querySelector('[data-stl-gallery-photo]');
        if (container) container.innerHTML = galleryImgHTML;
      }

      this.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('is-open'));
      });

      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('.pdp-stl-modal__close');
        if (closeBtn) closeBtn.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onKeydown);
      document.body.style.overflow = '';

      const isMobile = window.innerWidth < 750;
      setTimeout(() => {
        this.setAttribute('hidden', '');
        if (this._opener) {
          this._opener.focus();
          this._opener = null;
        }
      }, isMobile ? 340 : 240);
    }

    _onKeydown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key !== 'Tab') return;

      const focusable = Array.from(this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(el => el.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    _getVariants(item) {
      return JSON.parse(item.querySelector('[data-stl-variants]')?.textContent || '[]');
    }

    _resolveVariant(item) {
      /* Collect selected values per option position from checked radios */
      const selections = {};
      item.querySelectorAll('[data-stl-option-group]').forEach(group => {
        const checked = group.querySelector('.product-option__input:checked');
        if (checked) {
          const pos = parseInt(checked.dataset.optionPosition) - 1; // 0-based
          selections[pos] = checked.value;
        }
      });

      /* If any visible option group has no selection yet, don't resolve */
      const groupCount = item.querySelectorAll('[data-stl-option-group]').length;
      if (Object.keys(selections).length < groupCount) return null;

      const optKey = i => ['o1', 'o2', 'o3'][i];
      const matches = this._getVariants(item).filter(v =>
        Object.entries(selections).every(([idx, val]) => v[optKey(parseInt(idx))] === val)
      );

      return matches.find(v => v.available) ?? matches[0] ?? null;
    }

    _updateAtcBtn(item, variant) {
      const btn = item.querySelector('[data-stl-atc]');
      if (!btn) return;

      if (!variant) {
        /* No variant resolved yet — keep button disabled, reset to default text */
        btn.textContent = btn.dataset.addText;
        btn.disabled = true;
        btn.classList.remove('klaviyo-bis-trigger');
        delete btn.dataset.variantId;
        return;
      }

      btn.dataset.variantId = variant.id;

      if (variant.available) {
        btn.textContent = btn.dataset.addText;
        btn.disabled = false;
        btn.classList.remove('klaviyo-bis-trigger');
      } else {
        /* Sold-out — show BIS text and let Klaviyo handle the click if enabled */
        btn.textContent = btn.dataset.soldoutText || btn.dataset.addText;
        btn.disabled = false;
        if (btn.dataset.bisTrigger === 'true') {
          btn.classList.add('klaviyo-bis-trigger');
        }
      }
    }

    async _addToCart(btn) {
      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      const addText   = btn.dataset.addText   || btn.textContent.trim();
      const addedText = btn.dataset.addedText || '✓';

      btn.disabled = true;
      btn.classList.add('is-loading');

      /* Include section rendering so cart drawer HTML stays fresh */
      const sections = [];
      if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
      if (document.getElementById('cart-counter')) sections.push('cart-counter');

      try {
        const payload = { id: variantId, quantity: 1 };
        if (sections.length) {
          payload.sections     = sections;
          payload.sections_url = window.location.pathname;
        }

        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify(payload)
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `HTTP ${res.status}`);
        }

        const data = await res.json();

        trackShopTheLook('shop_the_look_add_to_cart', {
          source_product_id:     this._opener?.dataset.productId || null,
          source_product_handle: this._opener?.dataset.productHandle || null,
          product_id:            data.product_id,
          product_handle:        data.handle,
          product_title:         data.product_title,
          variant_id:            data.variant_id,
          price:                 data.final_price / 100,
          currency:              window.Shopify?.currency?.active || null
        });

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = addedText;

        this._refreshCart(data.sections);

        setTimeout(() => {
          btn.textContent = addText;
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 2200);

      } catch (err) {
        console.error('[STL] Add to cart failed:', err.message);
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    _refreshCart(renderedSections) {
      /* Re-render cart drawer body so it shows the newly added item */
      if (renderedSections) {
        const parser = new DOMParser();

        if (renderedSections['cart-drawer']) {
          const target = document.querySelector('#CartDrawer-Body');
          if (target) {
            const doc = parser.parseFromString(renderedSections['cart-drawer'], 'text/html');
            const src = doc.querySelector('#CartDrawer-Body');
            if (src) target.innerHTML = src.innerHTML;
          }
        }

        if (renderedSections['cart-counter']) {
          const target = document.querySelector('#shopify-section-cart-counter');
          if (target) {
            const doc = parser.parseFromString(renderedSections['cart-counter'], 'text/html');
            const src = doc.querySelector('#shopify-section-cart-counter');
            if (src) target.innerHTML = src.innerHTML;
          }
        }
      }

      if (typeof updateCartCounters === 'function') updateCartCounters();

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    }
  }

  customElements.define('pdp-shop-the-look-modal', PdpShopTheLookModal);
}

/* ── Trigger button: open the modal and capture the active gallery slide ── */
document.addEventListener('click', e => {
  const trigger = e.target.closest('[data-pdp-stl-trigger]');
  if (!trigger) return;

  const modal = document.getElementById(trigger.dataset.pdpStlTrigger);
  if (!modal) return;

  let galleryImgHTML = null;
  const activeImg = document.querySelector(
    '.main-product__media--slider .swiper-slide-active .media img, ' +
    '.main-product__media--slider .swiper-slide-active img'
  );
  if (activeImg) galleryImgHTML = activeImg.outerHTML;

  modal.open(trigger, galleryImgHTML);

  trackShopTheLook('shop_the_look_click', {
    product_id:     trigger.dataset.productId || null,
    product_handle: trigger.dataset.productHandle || null,
    product_title:  trigger.dataset.productTitle || null,
    item_count:     modal.querySelectorAll('[data-stl-item]').length
  });
});
